import { useMemo, useState } from "react";
import { useFormik } from "formik";
import { Alert, Form } from "antd";
import { Eye, EyeOff } from "lucide-react";
import AppInput from "../../../../components/ui/AppInput";
import AppButton from "../../../../components/ui/AppButton";
import PasswordPolicyHint from "../../../../components/common/PasswordPolicyHint";
import { newPasswordStepInitialValues, newPasswordStepSchema } from "../ForgotPasswordForm.schema";

/**
 * NewPasswordStep — step 3.
 *
 * Two-field form with the BRD password-policy schema + live strength meter.
 * Submits the new password (already pre-validated client side) up to the
 * orchestrator, which SHA-512-hashes it before calling
 * {@code /auth/forgot-password/complete}.
 */
export default function NewPasswordStep({ submitting, errorMessage, onSubmit }) {
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik({
    initialValues: newPasswordStepInitialValues,
    validationSchema: newPasswordStepSchema,
    onSubmit: async (values) => {
      await onSubmit(values.newPassword);
    },
  });

  const strength = useMemo(
    () => scoreStrength(formik.values.newPassword),
    [formik.values.newPassword],
  );
  const strengthLabel = ["Very weak", "Weak", "Fair", "Strong", "Very strong"][strength];
  const strengthColors = ["#B5511C", "#C4882A", "#4A6FA5", "#4A7C59", "#0E3D2F"];

  return (
    <Form
      layout="vertical"
      onFinish={formik.handleSubmit}
      requiredMark={false}
      className="mt-8 space-y-4"
    >
      <p className="text-sm text-mahogany">
        Set a new password. Must be ≥8 characters with upper, lower, digit and a special character.
      </p>

      <AppInput
        id="forgot-new-password"
        name="newPassword"
        type={showPw ? "text" : "password"}
        label="New password"
        labelExtra={<PasswordPolicyHint />}
        required
        size="large"
        autoComplete="new-password"
        value={formik.values.newPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        suffix={<EyeToggle on={showPw} onClick={() => setShowPw((v) => !v)} />}
        restrictLeadingSpace={false}
        restrictConsecutiveSpaces={false}
        status={formik.touched.newPassword && formik.errors.newPassword ? "error" : undefined}
        error={formik.touched.newPassword ? formik.errors.newPassword : undefined}
      />

      {formik.values.newPassword && (
        <div className="space-y-1">
          <div className="h-1.5 w-full bg-border rounded">
            <div
              className="h-1.5 rounded transition-all"
              style={{
                width: `${((strength + 1) / 5) * 100}%`,
                background: strengthColors[strength],
              }}
            />
          </div>
          <div className="text-xs" style={{ color: strengthColors[strength] }}>
            {strengthLabel}
          </div>
        </div>
      )}

      <AppInput
        id="forgot-confirm-password"
        name="confirmPassword"
        type={showConfirm ? "text" : "password"}
        label="Confirm password"
        required
        size="large"
        autoComplete="new-password"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        suffix={<EyeToggle on={showConfirm} onClick={() => setShowConfirm((v) => !v)} />}
        restrictLeadingSpace={false}
        restrictConsecutiveSpaces={false}
        status={
          formik.touched.confirmPassword && formik.errors.confirmPassword ? "error" : undefined
        }
        error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
      />

      {errorMessage && <Alert type="error" showIcon message={errorMessage} role="alert" />}

      <AppButton
        htmlType="submit"
        type="primary"
        size="large"
        block
        loading={submitting}
        disabled={submitting}
        label="Reset password"
      />
    </Form>
  );
}

function EyeToggle({ on, onClick }) {
  return (
    <AppButton
      type="text"
      size="small"
      onClick={onClick}
      aria-label={on ? "Hide password" : "Show password"}
      className="!p-0 !min-w-0 !h-auto !bg-transparent text-mahogany hover:text-cocoa"
      label={on ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    />
  );
}

function scoreStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(4, score - 1 < 0 ? 0 : score - 1);
}
